import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ReactComponent as EditIcon } from "../../../../../assets/edit-pencil.svg";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  TabId,
  TabSectionKey,
  TranslationManageAttributeResult,
} from "../../../../../services/api/types/portalConfiguration/translations";
import { brand, gray } from "../../../../../theme/themePrimitives";
import { EditModal } from "./components/EditModal";

interface TranslationTabSectionContentProps {
  attributes: TranslationManageAttributeResult[];
  images: string[];
  sectionLabel: string;
  tabId: TabId;
  tabSectionKey: TabSectionKey;
  isMain: boolean;
  onChanged: () => void;
}

export const TranslationTabSectionContent: React.FC<
  TranslationTabSectionContentProps
> = ({
  attributes,
  images,
  sectionLabel,
  tabId,
  tabSectionKey,
  isMain,
  onChanged,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedAttribute, setSelectedAttribute] =
    useState<TranslationManageAttributeResult>();
  const [headerExpanded, setHeaderExpanded] = useState(true);
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    setImageIndex(0);
  }, [tabId, tabSectionKey]);

  const showPager = images.length > 1;
  const currentImage = images[imageIndex] ?? images[0];
  const isAtStart = imageIndex === 0;
  const isAtEnd = imageIndex === images.length - 1;

  const goToPreviousImage = () => setImageIndex((i) => Math.max(i - 1, 0));
  const goToNextImage = () =>
    setImageIndex((i) => Math.min(i + 1, images.length - 1));

  return (
    <>
      <Box
        sx={{
          bgcolor: "background.paper",
          pb: 2,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {sectionLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${attributes.length} texts`}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              "&:hover": { cursor: "pointer" },
            }}
            onClick={() => setHeaderExpanded((v) => !v)}
          >
            <Typography fontWeight={"bold"} sx={{ color: brand[600] }}>
              {headerExpanded ? "Hide preview" : "Show preview"}
            </Typography>
            {headerExpanded ? (
              <ExpandLessIcon sx={{ color: brand[600] }} />
            ) : (
              <ExpandMoreIcon sx={{ color: brand[600] }} />
            )}
          </Box>
        </Box>
        <Collapse in={headerExpanded}>
          <Typography variant="body2" color="text.secondary">
            {`Translate the text that appears in ${sectionLabel}. The preview shows where these strings are used.`}
          </Typography>
          <Box pt={2}>
            <Box>
              <Box
                component="img"
                src={currentImage}
                alt={`${sectionLabel} preview`}
                sx={{
                  display: "block",
                  width: "400px",
                  aspectRatio: "1448 / 772",
                  borderRadius: "8px 8px 0 0",
                  objectFit: "cover",
                }}
              />
              {showPager && (
                <Box
                  sx={{
                    width: "400px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderRadius: "0 0 8px 8px",
                    border: "1px solid #EAECF0",
                    padding: "0.5rem",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={goToPreviousImage}
                    disabled={isAtStart}
                    aria-label="Previous image"
                    sx={{
                      bgcolor: brand[600],
                      color: "white",
                      "&:hover": { bgcolor: brand[700] },
                      "&.Mui-disabled": {
                        bgcolor: gray[200],
                        color: gray[400],
                      },
                      borderRadius: "12px",
                      border: "none",
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 32, textAlign: "center" }}
                  >
                    {imageIndex + 1}/{images.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={goToNextImage}
                    disabled={isAtEnd}
                    aria-label="Next image"
                    sx={{
                      bgcolor: brand[600],
                      color: "white",
                      "&:hover": { bgcolor: brand[700] },
                      "&.Mui-disabled": {
                        bgcolor: gray[200],
                        color: gray[400],
                      },
                      borderRadius: "12px",
                      border: "none",
                    }}
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        English (Base)
      </Typography>
      {attributes.map((attribute) => {
        const baseEnglish = attribute.values.en;
        const isMobileOnly = attribute.attributeKey.startsWith("mobile");

        return (
          <React.Fragment key={attribute.attributeKey}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: brand[50],
                    color: brand[700],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {attribute.attributeOrder && attribute.attributeOrder < 100000
                    ? attribute.attributeOrder
                    : ""}
                </Box>
                {isMobileOnly && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    (Only on mobile device)
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => {
                  setOpenModal(true);
                  setSelectedAttribute(attribute);
                }}
              >
                <EditIcon style={{ color: brand[600] }} />
                <Typography fontWeight={"bold"} color={brand[600]}>
                  Edit translation
                </Typography>
              </Box>
            </Box>
            <TextField
              fullWidth
              type="text"
              disabled
              value={baseEnglish?.effectiveValue ?? ""}
              variant="outlined"
              size="small"
            />
            <Divider sx={{ mt: 2 }} />
          </React.Fragment>
        );
      })}

      {selectedAttribute && (
        <EditModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          tabId={tabId}
          tabSectionKey={tabSectionKey}
          attribute={selectedAttribute}
          isMain={isMain}
          onSaved={onChanged}
        />
      )}
    </>
  );
};
